import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { History, User, Calendar, ArrowRight, Clock } from 'lucide-react';
import { getApiUrl } from '@/config';

interface ToolHistoryItem {
    id: string;
    toolId: string;
    technicianId: string;
    technicianName: string;
    assignedAt: string;
    returnedAt?: string;
}

interface ToolHistoryListProps {
    toolId?: string;
}

export const ToolHistoryList: React.FC<ToolHistoryListProps> = ({ toolId }) => {
    const [history, setHistory] = useState<ToolHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!toolId || toolId.startsWith('new-')) return; // Don't fetch for new tools

        const fetchHistory = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const headers: Record<string, string> = {};
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const res = await fetch(getApiUrl(`/api/tools/${toolId}/history`), { headers });
                if (res.ok) {
                    const data = await res.json();
                    setHistory(data);
                } else {
                    setError('Impossible de charger l\'historique');
                }
            } catch (err) {
                setError('Erreur réseau');
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [toolId]);

    if (!toolId) {
        return <div className="text-center p-8 text-gray-500">Enregistrez l'outil pour voir l'historique.</div>;
    }

    if (loading) {
        return <div className="text-center p-8 text-gray-500 animate-pulse">Chargement de l'historique...</div>;
    }

    if (error) {
        return <div className="text-center p-8 text-red-400">{error}</div>;
    }

    if (history.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <History size={48} className="mb-4 opacity-20" />
                <p>Aucun historique d'emprunt pour cet outil.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {history.map((entry) => {
                const isCurrent = !entry.returnedAt;
                return (
                    <div key={entry.id} className={`glass-panel p-4 flex gap-4 ${isCurrent ? 'border-l-4 border-l-primary bg-primary/5' : 'opacity-80'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCurrent ? 'bg-primary/20 text-primary' : 'bg-white/5 text-gray-400'}`}>
                            <User size={18} />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                                <h4 className={`font-semibold ${isCurrent ? 'text-white' : 'text-gray-400'}`}>
                                    {entry.technicianName}
                                </h4>
                                {isCurrent && (
                                    <span className="text-[10px] uppercase font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">En cours</span>
                                )}
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                <div className="flex items-center gap-1.5 min-w-[120px]">
                                    <Calendar size={14} className="text-emerald-500/70" />
                                    <span>{format(new Date(entry.assignedAt), 'dd MMM yyyy', { locale: fr })}</span>
                                    <span className="text-xs opacity-50">{format(new Date(entry.assignedAt), 'HH:mm', { locale: fr })}</span>
                                </div>

                                <ArrowRight size={14} className="opacity-30" />

                                {entry.returnedAt ? (
                                    <div className="flex items-center gap-1.5">
                                        <Clock size={14} className="text-amber-500/70" />
                                        <span>{format(new Date(entry.returnedAt), 'dd MMM yyyy', { locale: fr })}</span>
                                        <span className="text-xs opacity-50">{format(new Date(entry.returnedAt), 'HH:mm', { locale: fr })}</span>
                                    </div>
                                ) : (
                                    <span className="text-gray-600 italic">Non retourné</span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
